import { useCallback, useState, type Dispatch, type SetStateAction } from "react";
import { buildOrderRows } from "./components/menu/orderSummaryUtils";
import type { OrderLineChoice } from "./components/menu/EditModifiersModal";

interface OrderItem {
  itemId: number;
  quantity: number;
  modifiers: number[];
  addons: Record<number, Record<number, number>>;
  comment: string;
}

export interface OrderLineEditTarget {
  /** Индексите в кошницата, които редакцията покрива (обобщен ред → няколко). */
  indices: number[];
  itemId: number;
  /** Сборното количество на покритите записи. */
  quantity: number;
  modifiers: number[];
  addons: Record<number, Record<number, number>>;
  comment: string;
  /** Фокусът при отваряне: коментар от прегледа, количество от „Промени". */
  focusField: "comment" | "quantity";
}

interface UseOrderLineEditArgs {
  orderItems: OrderItem[];
  setOrderItems: Dispatch<SetStateAction<OrderItem[]>>;
  /** Дневният лимит на обедните продукти (useLunchLimits). */
  allowedQuantity: (itemId: number, wanted: number) => number;
  /** Не стигат ли порциите (и на добавките) за кошницата след редакцията. */
  cartShortage?: (nextCart: OrderItem[]) => boolean;
}

/**
 * Редакцията през EditModifiersModal (количество + добавки + модификатори +
 * коментар): моливът в „Преглед на поръчката" (openFromPanel) и „Промени" /
 * F9 за избрания в каталога продукт (openFromCatalog).
 */
export const useOrderLineEdit = ({
  orderItems,
  setOrderItems,
  allowedQuantity,
  cartShortage,
}: UseOrderLineEditArgs) => {
  const [target, setTarget] = useState<OrderLineEditTarget | null>(null);
  const close = useCallback(() => setTarget(null), []);

  const openForIndices = (
    indices: number[],
    focusField: OrderLineEditTarget["focusField"],
  ) => {
    const representative = orderItems[indices[0]];
    if (!representative) return;
    setTarget({
      indices,
      itemId: representative.itemId,
      quantity: indices.reduce(
        (sum, index) => sum + (orderItems[index]?.quantity ?? 0),
        0,
      ),
      modifiers: representative.modifiers,
      addons: representative.addons,
      comment: representative.comment ?? "",
      focusField,
    });
  };

  const openFromPanel = (indices: number[]) =>
    openForIndices(indices, "comment");

  // Каталогът показва сборно количество по продукт, а записите може да са
  // няколко (различни модификатори/коментар/добавки). „Промени" редактира
  // ПОСЛЕДНО маркирания запис заедно с напълно еднаквите му (обобщената му
  // група) — същия, в който се слива „+" от каталога.
  const openFromCatalog = (itemId: number) => {
    // (без findLastIndex — lib-ът е ES2022)
    let lastIndex = orderItems.length - 1;
    while (lastIndex >= 0 && orderItems[lastIndex].itemId !== itemId) lastIndex--;
    if (lastIndex < 0) return;
    const group = buildOrderRows(orderItems, true).find((row) =>
      row.indices.includes(lastIndex),
    );
    openForIndices(group?.indices ?? [lastIndex], "quantity");
  };

  // Добавките, модификаторите и коментарът се пишат във ВСИЧКИ покрити
  // записи, за да остане групата слята (и трите са част от ключа за
  // обобщаване); празен коментар го маха. Количеството: разликата се добавя
  // към последния запис, а намалението се взима от последните назад
  // (изпразнен запис отпада) — редът на маркиране се пази.
  const save = (data: OrderLineChoice) => {
    if (!target) return;
    const { indices, itemId } = target;
    const currentTotal = indices.reduce(
      (sum, index) => sum + (orderItems[index]?.quantity ?? 0),
      0,
    );
    let targetTotal = data.quantity;
    if (targetTotal > currentTotal) {
      // Обеден продукт с дневен лимит: не повече от останалите порции
      targetTotal =
        currentTotal + allowedQuantity(itemId, targetTotal - currentTotal);
    }

    const applyEdit = (current: OrderItem[]) => {
      const updated = current.map((orderItem, index) =>
        indices.includes(index)
          ? {
              ...orderItem,
              modifiers: data.modifiers,
              addons: data.addons,
              comment: data.comment,
            }
          : orderItem,
      );
      const lastIndex = indices[indices.length - 1];
      let diff = targetTotal - currentTotal;
      if (diff > 0) {
        updated[lastIndex] = {
          ...updated[lastIndex],
          quantity: updated[lastIndex].quantity + diff,
        };
      }
      const removed = new Set<number>();
      for (let i = indices.length - 1; i >= 0 && diff < 0; i--) {
        const index = indices[i];
        const record = updated[index];
        if (record.quantity > -diff) {
          updated[index] = { ...record, quantity: record.quantity + diff };
          diff = 0;
        } else {
          diff += record.quantity;
          removed.add(index);
        }
      }
      return updated.filter((_, index) => !removed.has(index));
    };
    // Дневно меню: добавка, сменена в редакцията, може да е изчерпана
    if (cartShortage?.(applyEdit(orderItems))) return;
    setOrderItems(applyEdit);
  };

  return { target, close, openFromPanel, openFromCatalog, save };
};
